<script setup>
import { translateToUI } from '@sensehawk/expression-editor';
import { watchDebounced } from '@vueuse/core';
import DOMPurify from 'dompurify';
import { cloneDeep, uniqBy } from 'lodash-es';
import { storeToRefs } from 'pinia';
import { onMounted } from 'vue';
import { useModal } from 'vue-final-modal';
import BiDatasetPickerSeries from '~/bi/components/dataset-picker/bi-dataset-picker-series.vue';
import BiUnPivotPopup from '~/bi/components/unpivot/bi-unpivot-popup.vue';
import { useBIQueryBuilder } from '~/bi/composables/useBIQueryBuilder.js';
import { useBiStore } from '~/bi/store/bi.store';
import HawkLoader from '~/common/components/atoms/hawk-loader.vue';
import { useCommonImports } from '~/common/composables/common-imports.composable';

const { $t } = useCommonImports();
const bi_store = useBiStore();

const { widget_builder_config, all_schemas, all_schema_columns } = storeToRefs(bi_store);
const { stages } = toRefs(widget_builder_config.value.query);

const { open: openBiPivotPopup, close: closeBiPivotPopup, patchOptions: patchPivotOptions } = useModal({
  component: BiUnPivotPopup,
});

const { open: openSeriesPopup, close: closeSeriesPopup, patchOptions: patchSeriesOptions } = useModal({
  component: BiDatasetPickerSeries,
});

function openPivotPopup(data) {
  patchPivotOptions(
    {
      attrs: {
        data,
        onClose() { closeBiPivotPopup(); },
        onSave(unpivot_data) {
          stages.value[stages.value.length - 1].unpivot = unpivot_data;
          closeBiPivotPopup();
        },
      },
    },
  );
  openBiPivotPopup();
}

const stage_options = [
  { label: $t('Unpivot results'), on_click: () => openPivotPopup() },
];

const { getIconsForType, createDefaultSelection, constructFieldName, constructColumnAlias, getColumnsForSeries } = useBIQueryBuilder();

const join_types = getIconsForType('joins');

const is_add_stage_disabled = computed(() => stages.value[stages.value.length - 1]?.columns?.filter(col => col?.is_aggregation)?.length === 0);

watchDebounced(() => [stages.value, bi_store.selected_stage_index], (new_val, old_val) => {
  bi_store.widget_builder_config_change_detector++;
  if (new_val[1] != null && new_val[1] === old_val[1]) {
    bi_store.selected_stage_index = null;
  }
}, { deep: true, debounce: 100 });

function addStage() {
  stages.value.push(createDefaultSelection());
}

function updateSeriesData() {
  const stage = stages.value[0];
  patchSeriesOptions({
    attrs: {
      asPopup: true,
      title: $t('Update series'),
      submitLabel: $t('Update'),
      seriesData: stage.table,
      onClose() { closeSeriesPopup(); },
      onSave(series) {
        stage.table = {
          ...stage.table,
          generateSeries: series.generateSeries,
          name: series.name,
          alias: series.alias,
        };
        closeSeriesPopup();
      },
    },
  });
  openSeriesPopup();
}

function getTablesFromStageFields(stage_index, keep_hidden_columns) {
  const fieldData = field => ({
    ...field,
    label: field.alias,
    type: field.field_type,
    field: field.alias,
    alias: field.alias,
  });

  const expressionData = field => ({
    ...field,
    label: field.alias,
    type: field.field_type,
    field: field.alias,
    alias: field.alias,
  });

  const previous_stage_columns = {};
  const ignored_stages_indexes = stages.value.slice(0, stage_index).map((stage, index) => stage.ignorePreviousStageColumns ? index : null).filter(index => index !== null);
  const last_ignored_stage_index = ignored_stages_indexes[ignored_stages_indexes.length - 1] || null;

  stages.value.forEach((stage, index) => {
    if (index < stage_index && index >= last_ignored_stage_index) {
      stage.columns.forEach((column) => {
        if (column.skip && !keep_hidden_columns)
          delete previous_stage_columns[column.alias];
        else
          previous_stage_columns[column.alias] = column;
      });
    }
  });

  const columns = uniqBy(Object.values(previous_stage_columns).map(field => ({
    ...(field.expr ? expressionData(field) : fieldData(field)),
  })), 'alias');

  const tables = [{
    label: ``,
    name: '',
    columns,
  }];
  return tables;
}

function removeSelectionFromStage(stage, stage_index) {
  const tables = getStageTables(stage_index);
  const column_fields = tables.map(table => table.columns.map(column => column.field)).flat();
  const table_column_alias = tables.map(table => table.columns.map(column => constructColumnAlias({ table_name: (table.name === stages.value[0].table || !table.name) ? null : table.label, column_name: column.label }))).flat();
  getStagePreviousFields(stage_index).forEach((column) => {
    column_fields.push(column.alias);
    column_fields.push(constructFieldName({ label: column.alias }, 'results'));
  });

  if (!stage.columns?.length)
    return;

  // Work on a mutable copy and repeatedly prune until stable to
  // ensure cascading deletions of dependent/derived columns.
  const current = stage.columns.slice();
  let changed = true;

  while (changed) {
    changed = false;
    // Recompute aliases present in the current set
    const current_aliases = current.map(f => f.alias);

    // Helper to determine if a referenced field is still available
    const is_includes = (f) => {
      return column_fields.includes(f) || current_aliases.includes(f) || table_column_alias.includes(f);
    };

    // Remove non-expression columns that no longer exist in available table columns
    for (let i = current.length - 1; i >= 0; i--) {
      const field = current[i];
      if (!field.expr) {
        const keep = column_fields.includes(field.field);
        if (!keep) {
          current.splice(i, 1);
          changed = true;
        }
      }
    }

    // Remove expression columns that reference any missing field (cascade handled by loop)
    for (let i = current.length - 1; i >= 0; i--) {
      const field = current[i];
      if (field.expr) {
        const refs = field.fields || [];
        const keep = refs.length === 0 || refs.every(is_includes);
        if (!keep) {
          current.splice(i, 1);
          changed = true;
        }
      }
    }
  }

  // Preserve original ordering of remaining fields
  stage.columns = current;

  // Update filters and orderBy based on remaining columns
  const remaining_aliases = stage.columns.map(field => field.alias);
  if (stage.filters?.length) {
    stage.filters = stage.filters.filter(field => field.expr || remaining_aliases.includes(field.field) || column_fields.includes(field.field));
  }

  if (stage.orderBy?.length) {
    stage.orderBy = stage.orderBy.filter(field => remaining_aliases.includes(field.column));
  }
}

function filterPreviousTables(joins, stage_index) {
  let current_table_columns;
  if (stage_index > 0) {
    const tables = getTablesFromStageFields(stage_index, true);
    current_table_columns = tables[0].columns;
  }
  else {
    current_table_columns = [];
  }
  current_table_columns = current_table_columns.map(column => column.field);
  const getConditions = join => join.on?.reduce((acc, condition) => {
    acc.push(condition.left);
    return acc;
  }, []) || [];
  return joins.filter(join => getConditions(join).every(column => current_table_columns.includes(column) || current_table_columns.includes(column.split('results→')?.[1])));
}

function syncPipelineFrom(index, should_remove_selection = false) {
  if (should_remove_selection) {
    removeSelectionFromStage(stages.value[index], index);
  }
  for (let i = (index + 1); i < stages.value.length; i++) {
    stages.value[i].joins = [...filterPreviousTables(stages.value[i].joins, i)];
    if (should_remove_selection)
      removeSelectionFromStage(stages.value[i], i);
  }
}

function renameAlias(data) {
  let { from, to } = data;
  from = from.trim('');
  to = to.trim('');
  const stages_clone = cloneDeep(stages.value);
  stages_clone.forEach((stage, stage_index) => {
    // Stage columns
    stage.columns.forEach((column) => {
      if (column.field === constructFieldName({ label: from }, 'results')) {
        column.field = constructFieldName({ label: to }, 'results');
      }
      if (column.field === from) {
        column.field = to;
        column.label = to;
      }
      if (column.alias === from) {
        column.alias = to;
      }
      if (column.expr && column.fields.includes(from)) {
        column.fields = column.fields.map(field => field === from ? to : field);
        column.expr = column.expr.replaceAll(`[${from}]`, `[${to}]`);
      }
      if (column.expr && column.fields.includes(constructFieldName({ label: from }, 'results'))) {
        column.fields = column.fields.map(field => field === constructFieldName({ label: from }, 'results') ? constructFieldName({ label: to }, 'results') : field);
        column.expr = column.expr.replaceAll(`[${constructFieldName({ label: from }, 'results')}]`, `[${constructFieldName({ label: to }, 'results')}]`);
      }
    });

    // Stage filters
    stage.filters.forEach((filter) => {
      if (filter.field === from) {
        filter.field = to;
      }
      if (filter.field === constructFieldName({ label: from }, 'results')) {
        filter.field = constructFieldName({ label: to }, 'results');
      }
    });

    // Stage orderBy
    stage.orderBy.forEach((column) => {
      if (column.column === from) {
        column.column = to;
      }
    });
    // Table columns
    if (stage_index > 0) {
      stage.joins.forEach((join) => {
        if (join.condition) {
          join.condition = join.condition.replaceAll(`[${constructFieldName({ label: from }, 'results')}]`, `[${constructFieldName({ label: to }, 'results')}]`);
        }
        join?.on?.forEach((condition) => {
          if (condition.left === from) {
            condition.left = to;
            condition.left_label = to;
          }
        });
      });
    }
  });
  stages.value = stages_clone;
}

function onFieldsAdded(index) {
  syncPipelineFrom(index);
}

function onFieldsDeleted(index) {
  syncPipelineFrom(index, true);
  stages.value = stages.value.filter((stage, index) => index === 0 || stages.value[index - 1].columns.length);
}
const stage_tables = computed(() => stages.value.map((stage, index) => getTablesForStage(stage, index)));

// Cache previous-stage fields per stage to avoid re-computing on every render
const stage_previous_fields = computed(() => stages.value.map((_, index) => {
  if (index > 0) {
    const tables = getTablesFromStageFields(index);
    return tables[0].columns;
  }
  return [];
}));

const getExpressionColumnsForField = stage_index => stage_previous_fields.value[stage_index].map(column => ({ label: constructColumnAlias({ table_name: 'Previous stage', column_name: column.alias }), name: constructFieldName({ label: column.alias, table_name: 'results' }), type: column.type, path: column.path }));
const getExpressionColumnsForTable = (stage_index, table_name) => stage_tables.value[stage_index].flatMap(table => table.columns?.map(column => ({ label: column.label, name: column.field, type: column.type, path: table_name === table.name ? [table.label, ...(column.path || [])] : column.path })));
const getExpressionColumnsLabels = (stage_index, table_name) => [...getExpressionColumnsForTable(stage_index, table_name), ...getExpressionColumnsForField(stage_index)];

function getJoinConditionText(table, stage_index) {
  return table.condition ? translateToUI(table.condition, getExpressionColumnsLabels(stage_index, table.table.name)) : table.on.map((condition, index) => `${index === 0 ? '' : (condition.logic || '')} ${condition.left_label} ${condition.op || '='} ${condition.right_label}`).join(' ');
}

function getTooltipContent(table, stage_index) {
  let tooltip_text = '';
  if (table.condition)
    return `<div class="text-xs font-semibold text-gray-700 my-1">${translateToUI(table.condition, getExpressionColumnsLabels(stage_index, table.table.name))}</div>`;
  tooltip_text = table.on?.map((condition, index) => {
    const logic_html = condition.logic && index > 0 ? `<div class="text-xs font-semibold text-gray-700 my-1">${condition.logic}</div>` : '';
    const condition_html = `<div class="text-xs font-medium text-gray-600">${condition.left_label} ${condition.op || '='} ${condition.right_label}</div>`;
    return logic_html + condition_html;
  });
  return tooltip_text?.length ? DOMPurify.sanitize(tooltip_text?.join('')) : '';
}

function getTablesForStage(stage, stage_index) {
  let tables = [];
  if (stage.table || stage_index === 0) {
    const table_label = stage.table?.name || bi_store.all_schema_by_name[stage.table].label;
    tables = [{ label: table_label, name: stage.table?.name || stage.table, columns: stage.table?.generateSeries ? getColumnsForSeries(stage.table) : all_schema_columns.value[stage.table] }];
  }
  stage.joins?.forEach((join) => {
    const table_label = bi_store.all_schema_by_name[join.table.name]?.label || join.table?.name;
    tables.push({ label: table_label, name: join.table.name, columns: join.table?.generateSeries ? getColumnsForSeries(join.table) : all_schema_columns.value[join.table.name] });
  });
  return tables;
}

function getStageTables(index) {
  return stage_tables.value[index] || [];
}

function getStagePreviousFields(index) {
  return stage_previous_fields.value[index] || [];
}

function updateStage(stage, event) {
  stage.columns = event.columns;
  stage.orderBy = event.orderBy;
  stage.filters = event.filters;
  stage.limit = event.limit;
}

const is_loading = ref(true);
onMounted(() => {
  setTimeout(() => {
    is_loading.value = false;
  }, 100);
});

function getAllVisibleColumns(stages) {
  const columns = stages.reduce((acc, stage) => {
    return acc.concat(stage.columns);
  }, []).flat();
  return columns.filter(column => !column.skip);
}
</script>

<template>
  <div>
    <HawkLoader v-if="is_loading" />
    <div v-show="!is_loading" class="w-full h-full">
      <div v-for="(stage, index) in stages" :key="index" class="mb-2 pb-2 ">
        <div v-if="stages.length > 1" class="flex justify-between items-center mb-2">
          <div class="text-xs font-medium text-gray-500 bg-gray-50 border border-gray-300 px-2 py-1 rounded-md">
            {{ $t('Stage') }} {{ index + 1 }}
          </div>
          <div class="gap-2 flex items-center">
            <hawk-button v-if="index > 0" v-tippy="stage.ignorePreviousStageColumns ? $t('Consider the columns from previous stages') : $t('Do not consider the columns from previous stages')" icon type="text" size="xs" :class="{ 'opacity-30': stage.ignorePreviousStageColumns }" @click="stage.ignorePreviousStageColumns = !stage.ignorePreviousStageColumns">
              <IconHawkChevronDownDouble class="size-4  min-w-4" />
            </hawk-button>
            <bi-query-builder-add-join :stage="stage" :stage-index="index" :tables="getStageTables(index)" :previous-stage-fields="getStagePreviousFields(index)" :all-tables="all_schemas" @save="stage.joins.push($event)" />
            <hawk-button v-if="bi_store.selected_stage_index === index" icon type="text" size="xs" @click="bi_store.selected_stage_index = null">
              <IconHawkStop class="fill-gray-700 size-4 min-w-4" />
            </hawk-button>
            <hawk-button v-else icon type="text" size="xs" @click="bi_store.selected_stage_index = index">
              <IconHawkPlay class="fill-gray-700 size-4  min-w-4" />
            </hawk-button>
            <hawk-button icon type="text" size="xs" :disabled="index === 0" @click="stages.splice(index, 1)">
              <IconHawkTrashThree class="text-gray-500 size-4  min-w-4" />
            </hawk-button>
          </div>
        </div>
        <div v-if="index === 0" class="text-sm text-gray-700 font-semibold flex items-center justify-between gap-1 group">
          <div class="flex items-center">
            <IconHawkDatabaseTwo class="size-4  min-w-4 mr-1" /> {{ stage.table?.name || bi_store.all_schema_by_name[stage.table]?.label }}
            <HawkButton v-if="stage.table?.generateSeries" icon type="text" class="hidden group-hover:block ml-2" size="xxs" @click="updateSeriesData">
              <IconHawkEditTwo class="size-4 text-gray-500" />
            </HawkButton>
          </div>
          <div v-if="stages.length === 1">
            <bi-query-builder-add-join :stage="stage" :stage-index="index" :tables="getStageTables(index)" :previous-stage-fields="getStagePreviousFields(index)" :all-tables="all_schemas" @save="stage.joins.push($event)" />
          </div>
        </div>
        <div v-if="stage.joins?.length > 0" class="pl-3">
          <div v-for="(join, join_index) in stage.joins" :key="join" class="pl-3  hover:bg-gray-50 rounded-md flex justify-between group">
            <bi-query-builder-add-join :stage="stage" :tables="getStageTables(index)" :previous-stage-fields="getStagePreviousFields(index)" :stage-index="index" :value="cloneDeep(join)" :all-tables="all_schemas" class="flex w-full py-2 relative" @save="stage.joins[join_index] = $event">
              <div class="text-left flex">
                <div class="border-l absolute -translate-y-[105%] border-gray-300 left-3" :class="{ 'h-3': join_index === 0, 'h-8': join_index > 0 }" />
                <div class="rounded-md p-1 w-6 h-6 flex items-center justify-center border">
                  <component :is="join_types[join.type]" class=" text-gray-600" />
                </div>
                <div class="w-4 h-0 border-t border-1 border-gray-300 mt-3 mx-2" />
                <div>
                  <div class="text-sm font-semibold text-gray-700">
                    {{ join.table.label }}
                  </div>
                  <div class="text-xs font-medium text-gray-600 mt-2 flex items-center">
                    {{ $t('on') }} <span v-tippy="{ content: getTooltipContent(join, index), placement: 'bottom', allowHTML: true, theme: 'my-custom-theme' }" class="border rounded-md p-1 truncate max-w-64 inline-block ml-1">{{ getJoinConditionText(join, index) }}</span>
                  </div>
                </div>
              </div>
            </bi-query-builder-add-join>
            <div class="p-2 hidden group-hover:block">
              <IconHawkXClose class="text-gray-500 hover:text-gray-700 cursor-pointer  min-w-4 size-4" @click="stage.joins.splice(join_index, 1);removeSelectionFromStage(stage, index);syncPipelineFrom(index, true)" />
            </div>
          </div>
        </div>
        <bi-query-builder-stage :model-value="stage" class="mt-2" :selected-table="stage.table?.name || stage.table" :stage-index="index" :ignore-previous-stage-columns="stage.ignorePreviousStageColumns" :tables="getStageTables(index)" :previous-stage-fields="getStagePreviousFields(index)" @update:model-value="updateStage(stage, $event)" @fields-deleted="onFieldsDeleted(index)" @fields-added="onFieldsAdded(index)" @rename="renameAlias" />
      </div>
      <div v-if="stages && stages[stages.length - 1] && !stages[stages.length - 1].unpivot" class="mt-2 pt-2 pb-4 relative ">
        <hawk-button
          type="light"
          :class="[is_add_stage_disabled ? '!border-primary-200' : '!border-primary-700']"
          block
          :disabled="is_add_stage_disabled" @click="addStage"
        >
          <IconHawkPlus /> {{ $t('Add stage') }}
        </hawk-button>
        <hawk-menu :items="stage_options" :disabled="getAllVisibleColumns(stages).length < 3" position="fixed" class="!absolute right-0 top-2" :class="{ 'opacity-50': getAllVisibleColumns(stages).length < 3 }" additional_trigger_classes="!ring-0 !focus:ring-0 !px-0 !py-0" additional_dropdown_classes="transition duration-150 ease-in-out">
          <template #trigger="{ open }">
            <hawk-button type="light" class="!rounded-tl-none !rounded-bl-none  p-2.5" :class="{ '!border-primary-400': is_add_stage_disabled, '!border-primary-700': !is_add_stage_disabled }">
              <IconHawkChevronDown class="w-4 h-4 ml-1" :class="{ 'rotate-180': open }" />
            </hawk-button>
          </template>
        </hawk-menu>
      </div>
      <div v-else-if="stages && stages[stages.length - 1] && stages[stages.length - 1].unpivot" class="border border-gray-200 p-2">
        <div class="flex items-center justify-between">
          <div class="text-xs font-medium text-gray-500 bg-gray-50 border border-gray-300 px-2 py-1 rounded-md">
            {{ $t('Unpivot') }}
          </div>
          <div>
            <hawk-button icon type="text" size="xs" @click="openPivotPopup(stages[stages.length - 1].unpivot)">
              <IconHawkSettingsOne class="size-4 text-gray-500" />
            </hawk-button>

            <hawk-button icon type="text" size="xs" @click="delete stages[stages.length - 1].unpivot">
              <IconHawkTrashThree class="text-gray-500 size-4  min-w-4" />
            </hawk-button>
          </div>
        </div>
        <div v-if="stages[stages.length - 1].unpivot.active_tab === 'basic'" class="mt-2 ml-1">
          <div class="text-sm font-medium text-gray-900">
            {{ $t('Columns') }}
          </div>
          <div v-for="row in stages[stages.length - 1].unpivot.rows" :key="row" class="text-gray-600 text-sm font-normal p-2">
            {{ row.title.field }}
          </div>
        </div>
        <div v-else class="mt-2 ml-1">
          <div v-for="row in stages[stages.length - 1].unpivot.rows" :key="row" class="text-sm font-medium text-gray-900">
            {{ row.title.field }}
            <div v-for="col in row.columns" :key="col" class="text-gray-600 text-sm font-normal p-2">
              {{ col.alias }} → {{ col.field }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
/* Using the ::v-deep selector */
.tippy-box[data-theme~='my-custom-theme'] {
  background-color: white;
  border: 1px solid #e0e0e0;
}

.tippy-box[data-theme~='my-custom-theme'] .tippy-arrow {
  border-bottom-color: white;
  display:none;
}
</style>
